import * as amplitude from "@amplitude/unified";

const AMPLITUDE_API_KEY = "94b72c57d3513b69603236e48574ceb7";

let initStarted = false;

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

/** Initialize Amplitude Analytics and Session Replay once per app lifecycle (client-only). */
export function initAmplitude(): void {
  if (!isBrowser() || initStarted) return;

  initStarted = true;

  void amplitude.initAll(AMPLITUDE_API_KEY, {
    analytics: { autocapture: true },
    sessionReplay: { sampleRate: 1 },
  });
}

export function trackEvent(
  eventName: string,
  eventProperties?: Record<string, unknown>,
): void {
  if (!isBrowser() || !initStarted) return;
  amplitude.track(eventName, eventProperties);
}

export function trackPageView(path: string): void {
  trackEvent("Page Viewed", { path });
}

export function setAmplitudeUserId(userId: string | undefined): void {
  if (!isBrowser() || !initStarted) return;
  amplitude.setUserId(userId);
}

export function resetAmplitudeUser(): void {
  if (!isBrowser() || !initStarted) return;
  amplitude.reset();
}

export type SubscriptionPlanAction = "start" | "upgrade" | "downgrade" | "trial";

/** "Your free trial is ready" popup. */
export function trackTrialPromoModalShown(): void {
  trackEvent("Trial Promo Modal Shown");
}
export function trackTrialPromoModalClicked(): void {
  trackEvent("Trial Promo Modal Clicked");
}
export function trackTrialPromoModalDismissed(): void {
  trackEvent("Trial Promo Modal Dismissed");
}

/** The "payment failed" banner was shown (once per page load). */
export function trackPaymentFailedBannerViewed(): void {
  trackEvent("Payment Failed Banner Viewed");
}

/** The user asked to retry a failed payment now. */
export function trackPaymentRetryRequested(properties: {
  source: "banner" | "subscription_card" | "card_change";
  outcome: "paid" | "failed" | "error";
}): void {
  trackEvent("Payment Retry Requested", properties);
}

/** Trial CTA rendered on the plans page for an eligible user (once per view). */
export function trackTrialOfferViewed(properties: {
  trial_days: number;
  plan_ids: number[];
}): void {
  trackEvent("Trial Offer Viewed", properties);
}

/** A free trial was started from checkout. */
export function trackTrialStarted(properties: {
  plan_id: number;
  plan_name: string;
  trial_days: number;
  charge_amount: number;
  has_promocode: boolean;
}): void {
  trackEvent("Trial Started", properties);
}

export function trackSubscriptionPlansViewed(): void {
  trackEvent("Subscription Plans Viewed");
}

export function trackSubscriptionPlanSelected(properties: {
  plan_id: number;
  plan_name: string;
  action: SubscriptionPlanAction;
  price: number;
}): void {
  trackEvent("Subscription Plan Selected", properties);
}

export function trackSubscriptionCheckoutViewed(properties: {
  plan_id: number;
  plan_name: string;
  price: number;
}): void {
  trackEvent("Subscription Checkout Viewed", properties);
}

export function trackSubscriptionCheckoutStarted(properties: {
  plan_id: number;
  plan_name: string;
  price: number;
}): void {
  trackEvent("Subscription Checkout Started", properties);
}

export function trackSubscriptionPaymentSubmitted(properties: {
  plan_id: number;
  plan_name: string;
  price: number;
  has_promocode: boolean;
}): void {
  trackEvent("Subscription Payment Submitted", properties);
}

export function trackSubscriptionCompleted(properties: {
  plan_id: number;
  plan_name: string;
  price: number;
}): void {
  trackEvent("Subscription Completed", properties);
}

export function trackSubscriptionFailed(properties: {
  plan_id: number;
  plan_name: string;
  price: number;
  error_message?: string;
}): void {
  trackEvent("Subscription Failed", properties);
}

export function trackAnnualDiscountPromoBarShown(): void {
  trackEvent("Annual Discount Promo Bar Shown");
}

export function trackAnnualDiscountPromoBarClicked(): void {
  trackEvent("Annual Discount Promo Bar Clicked");
}

export function trackAnnualDiscountPromoModalShown(): void {
  trackEvent("Annual Discount Promo Modal Shown");
}

export function trackAnnualDiscountPromoModalClicked(): void {
  trackEvent("Annual Discount Promo Modal Clicked");
}

export function trackAnnualDiscountPromoModalDismissed(): void {
  trackEvent("Annual Discount Promo Modal Dismissed");
}
